import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  AlertTitle,
  Link,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import {
  contextService,
  BulkRegisterContextInput,
  BulkRegisterResult,
  CONTEXT_TYPE_VALUES,
} from '@services/contextService';
import { organizationService } from '@services/organizationService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';

const MAX_CONTEXTS = 1000;
const VALID_TYPES: readonly string[] = CONTEXT_TYPE_VALUES;

// Column header aliases accepted in a CSV so migration exports from other tools
// "just work" without hand-editing.
const IDENTIFIER_HEADERS = ['canonical_identifier', 'identifier', 'url', 'place', 'address'];
const NAME_HEADERS = ['name', 'title', 'label'];
const TYPE_HEADERS = ['context_type', 'type'];
const DESCRIPTION_HEADERS = ['description', 'notes'];

// Downloadable samples (REV-453). Exported so a test can assert they always
// parse cleanly through parseBulkInput — i.e. the documented format and the
// parser can never drift apart.
export const SAMPLE_CSV = `canonical_identifier,name,context_type,description
https://www.example.com,Example Marketing Site,website,Primary marketing site
acme-corp/web-app,Acme Web App,repository,Main product repository
staging.example.com,Staging,website,Pre-production environment
`;

export const SAMPLE_JSON = `${JSON.stringify(
  {
    contexts: [
      {
        canonical_identifier: 'https://www.example.com',
        name: 'Example Marketing Site',
        context_type: 'website',
        description: 'Primary marketing site',
      },
      {
        canonical_identifier: 'acme-corp/web-app',
        name: 'Acme Web App',
        context_type: 'repository',
      },
    ],
  },
  null,
  2
)}\n`;

/** Trigger a client-side download of a text file (no server round-trip). */
function downloadTextFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file);
  });
}

// Parse a single CSV line, honoring double-quoted fields with embedded commas
// and escaped quotes ("").
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(field);
      field = '';
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out.map((f) => f.trim());
}

function pickColumn(headers: string[], aliases: string[]): number {
  return headers.findIndex((h) => aliases.includes(h));
}

/**
 * Parse pasted/uploaded text into bulk-registration rows. Auto-detects JSON
 * (a bare array or a `{ contexts: [...] }` document) vs CSV. Throws an Error
 * whose message is an i18n key on a structural problem.
 */
export function parseBulkInput(text: string): BulkRegisterContextInput[] {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('bulkRegister.errors.empty');
  }

  // JSON path.
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    let json: unknown;
    try {
      json = JSON.parse(trimmed);
    } catch {
      throw new Error('bulkRegister.errors.invalidJson');
    }
    const arr = Array.isArray(json) ? json : (json as { contexts?: unknown }).contexts;
    if (!Array.isArray(arr)) {
      throw new Error('bulkRegister.errors.invalidFormat');
    }
    const rows = arr.map((raw) => {
      const r = (raw ?? {}) as Record<string, unknown>;
      const identifier = r.canonical_identifier ?? r.identifier ?? r.url;
      return {
        canonical_identifier: typeof identifier === 'string' ? identifier.trim() : '',
        name: typeof r.name === 'string' ? r.name.trim() : undefined,
        context_type:
          typeof r.context_type === 'string' && VALID_TYPES.includes(r.context_type)
            ? (r.context_type as BulkRegisterContextInput['context_type'])
            : undefined,
        description: typeof r.description === 'string' ? r.description.trim() : undefined,
      };
    });
    return finalizeRows(rows);
  }

  // CSV path.
  const lines = trimmed.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length < 2) {
    throw new Error('bulkRegister.errors.csvNeedsHeader');
  }
  const headers = parseCsvLine(lines[0] ?? '').map((h) => h.toLowerCase());
  const idCol = pickColumn(headers, IDENTIFIER_HEADERS);
  if (idCol === -1) {
    throw new Error('bulkRegister.errors.csvNoIdentifierColumn');
  }
  const nameCol = pickColumn(headers, NAME_HEADERS);
  const typeCol = pickColumn(headers, TYPE_HEADERS);
  const descCol = pickColumn(headers, DESCRIPTION_HEADERS);

  const rows = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const typeValue = typeCol >= 0 ? cells[typeCol] : undefined;
    return {
      canonical_identifier: (cells[idCol] ?? '').trim(),
      name: nameCol >= 0 && cells[nameCol] ? cells[nameCol].trim() : undefined,
      context_type:
        typeValue && VALID_TYPES.includes(typeValue)
          ? (typeValue as BulkRegisterContextInput['context_type'])
          : undefined,
      description: descCol >= 0 && cells[descCol] ? cells[descCol].trim() : undefined,
    };
  });
  return finalizeRows(rows);
}

function finalizeRows(rows: BulkRegisterContextInput[]): BulkRegisterContextInput[] {
  const cleaned = rows.filter((r) => r.canonical_identifier !== '');
  if (cleaned.length === 0) {
    throw new Error('bulkRegister.errors.noRows');
  }
  if (cleaned.length > MAX_CONTEXTS) {
    throw new Error('bulkRegister.errors.tooMany');
  }
  return cleaned;
}

function BulkRegisterContextsPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [parsed, setParsed] = useState<BulkRegisterContextInput[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkRegisterResult | null>(null);

  const fetchOrg = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
    } catch {
      setError(t('bulkRegister.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void fetchOrg();
  }, [fetchOrg]);

  const applyParse = (text: string) => {
    setResult(null);
    setSubmitError(null);
    try {
      const rows = parseBulkInput(text);
      setParsed(rows);
      setParseError(null);
    } catch (e) {
      setParsed(null);
      setParseError(t((e as Error).message));
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setParsed(null);
    setParseError(null);
    let raw: string;
    try {
      raw = await readFileText(file);
    } catch {
      setParseError(t('bulkRegister.errors.readFailed'));
      return;
    }
    setPasteText(raw);
    applyParse(raw);
  };

  const handlePreview = () => {
    setFileName(null);
    applyParse(pasteText);
  };

  const handleRegister = async () => {
    if (!orgId || !parsed) return;
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    try {
      const summary = await contextService.bulkRegister(orgId, parsed);
      setResult(summary);
    } catch {
      setSubmitError(t('bulkRegister.errors.registerFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={280} height={48} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        {/* REV-732: goes to a URL, so it is a link. */}
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} component={RouterLink} to="/orgs">
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  const orgHref = `/orgs/${slug}`;
  const createdCount = result?.results.filter((r) => r.status === 'created').length ?? 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('bulkRegister.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        {t('bulkRegister.description')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('bulkRegister.uploadHeading')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bulkRegister.uploadHelp')}
          </Typography>

          {/* Format reference + downloadable samples (REV-453) */}
          <Box component="details" sx={{ mb: 2, '& > summary': { cursor: 'pointer', mb: 1 } }}>
            <Box component="summary">
              <Typography component="span" variant="subtitle2">
                {t('bulkRegister.format.heading')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('bulkRegister.format.intro', { max: MAX_CONTEXTS })}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0, mb: 1 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('bulkRegister.format.identifier')}</strong>{' '}
                  {t('bulkRegister.format.identifierDesc')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  {/* eslint-disable-next-line react/jsx-no-literals -- a CSV column name, which the file must use verbatim */}
                  <strong>name</strong> — {t('bulkRegister.format.nameDesc')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  {/* eslint-disable-next-line react/jsx-no-literals -- a CSV column name, which the file must use verbatim */}
                  <strong>context_type</strong> — {t('bulkRegister.format.typeDesc')} (
                  {VALID_TYPES.join(', ')})
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  {/* eslint-disable-next-line react/jsx-no-literals -- a CSV column name, which the file must use verbatim */}
                  <strong>description</strong> — {t('bulkRegister.format.descDesc')}
                </Typography>
              </li>
            </Box>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() =>
                  downloadTextFile('revenant-contexts-sample.csv', SAMPLE_CSV, 'text/csv')
                }
              >
                {t('bulkRegister.format.downloadCsv')}
              </Link>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() =>
                  downloadTextFile('revenant-contexts-sample.json', SAMPLE_JSON, 'application/json')
                }
              >
                {t('bulkRegister.format.downloadJson')}
              </Link>
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {t('bulkRegister.chooseFile')}
              <input
                type="file"
                hidden
                accept=".csv,.json,text/csv,application/json"
                aria-label={t('bulkRegister.chooseFile')}
                onChange={handleFileChange}
              />
            </Button>
            {fileName && (
              <Typography variant="body2" data-testid="selected-file">
                {fileName}
              </Typography>
            )}
          </Stack>

          <TextField
            label={t('bulkRegister.pasteLabel')}
            placeholder={t('bulkRegister.pastePlaceholder')}
            multiline
            minRows={4}
            fullWidth
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
          <Box sx={{ mt: 1 }}>
            <Button onClick={handlePreview} disabled={pasteText.trim() === ''}>
              {t('bulkRegister.preview')}
            </Button>
          </Box>

          {parseError && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {parseError}
            </Alert>
          )}

          {parsed && !parseError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('bulkRegister.ready', { count: parsed.length })}
              </Alert>
              <ScrollableTableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 2, maxHeight: 280 }}
              >
                <Table size="small" stickyHeader aria-label={t('bulkRegister.previewTableLabel')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('bulkRegister.colIdentifier')}</TableCell>
                      <TableCell>{t('bulkRegister.colName')}</TableCell>
                      <TableCell>{t('bulkRegister.colType')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.slice(0, 50).map((row, i) => (
                      <TableRow key={`${row.canonical_identifier}-${i}`}>
                        <TableCell>{row.canonical_identifier}</TableCell>
                        <TableCell>{row.name ?? '—'}</TableCell>
                        <TableCell>{row.context_type ?? t('bulkRegister.autoDetect')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollableTableContainer>
              {parsed.length > 50 && (
                <Typography variant="caption" color="text.secondary">
                  {t('bulkRegister.previewTruncated', { shown: 50, total: parsed.length })}
                </Typography>
              )}
              <Box>
                <Button
                  variant="contained"
                  onClick={handleRegister}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {submitting
                    ? t('bulkRegister.registering')
                    : t('bulkRegister.registerButton', { count: parsed.length })}
                </Button>
              </Box>
            </Box>
          )}

          {submitError && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('bulkRegister.summaryHeading')}
            </Typography>

            <Alert
              severity={result.failed > 0 || result.overLimit ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              role="status"
            >
              <AlertTitle>{t('bulkRegister.summaryTitle')}</AlertTitle>
              {t('bulkRegister.summaryBody', {
                created: result.created,
                submitted: result.submitted,
                skipped: result.skipped,
                failed: result.failed,
              })}
            </Alert>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              <Chip
                color="success"
                label={t('bulkRegister.chipCreated', { count: createdCount })}
              />
              {result.skipped > 0 && (
                <Chip label={t('bulkRegister.chipSkipped', { count: result.skipped })} />
              )}
              {result.failed > 0 && (
                <Chip
                  color="error"
                  label={t('bulkRegister.chipFailed', { count: result.failed })}
                />
              )}
            </Stack>

            {result.overLimit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('bulkRegister.overLimit', {
                  used: result.usage.used,
                  limit: result.usage.limit ?? '—',
                })}
              </Alert>
            )}

            {/* REV-493: optional-chained. The API does send `errors` today, but
                reading it unguarded means a single missing optional field
                white-screens the whole page after a successful registration —
                the user's places are created and they see nothing. */}
            {(result.errors?.length ?? 0) > 0 && (
              <ScrollableTableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label={t('bulkRegister.errorsTableLabel')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('bulkRegister.errorRow')}</TableCell>
                      <TableCell>{t('bulkRegister.errorReason')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((e) => (
                      <TableRow key={e.index}>
                        <TableCell>{e.name ?? `#${e.index + 1}`}</TableCell>
                        <TableCell>{e.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollableTableContainer>
            )}

            {/* REV-732: goes to a URL, so it is a link. */}
            <Button sx={{ mt: 2 }} variant="outlined" component={RouterLink} to={orgHref}>
              {t('bulkRegister.backToOrg')}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default BulkRegisterContextsPage;
